import { useSEO, generateWebsiteJsonLd } from '@/hooks/useSEO';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { MapPin, Users, BookOpen, Trophy, Settings, Shield, FileText, HelpCircle } from 'lucide-react';

export default function Sitemap() {
  // SEO Configuration
  useSEO({
    title: "ChessHatch Sitemap - Complete Site Navigation | ChessHatch",
    description: "Navigate all ChessHatch features easily. Find chess tutorials, puzzles, analysis tools, community forums, and account settings in one organized location.",
    keywords: "chesshatch sitemap, site navigation, chess platform map, find chess features, website structure",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SiteNavigationElement",
      "name": "ChessHatch Sitemap",
      "description": "Complete navigation map for ChessHatch chess learning platform",
      "url": "https://chesshatch.co/sitemap",
      ...generateWebsiteJsonLd()
    }
  });

  const siteStructure = {
    "Learning & Playing": [
      { path: "/", title: "Home", description: "Start your chess journey", icon: MapPin },
      { path: "/tutorials", title: "Chess Tutorials", description: "Learn chess fundamentals step by step", icon: BookOpen },
      { path: "/puzzles", title: "Chess Puzzles", description: "Practice tactics and strategy", icon: HelpCircle },
      { path: "/analysis", title: "Game Analysis", description: "Analyze positions with chess engine", icon: Settings }
    ],
    "Community & Competition": [
      { path: "/forums", title: "Community Forums", description: "Connect with chess players worldwide", icon: Users },
      { path: "/leaderboards", title: "Leaderboards", description: "See top players and rankings", icon: Trophy },
      { path: "/private-rooms", title: "Private Rooms", description: "Play chess with friends", icon: Users }
    ],
    "Account & Profile": [
      { path: "/auth", title: "Sign In", description: "Access your chess account", icon: Settings },
      { path: "/profile/avatar", title: "Avatar Editor", description: "Customize your chess avatar", icon: Settings },
      { path: "/forgot-password", title: "Password Recovery", description: "Reset your account password", icon: Shield },
      { path: "/reset-password", title: "Set New Password", description: "Update account security", icon: Shield }
    ],
    "About & Policies": [
      { path: "/about", title: "About ChessHatch", description: "Learn about our chess platform", icon: FileText },
      { path: "/terms", title: "Terms of Service", description: "Platform usage terms", icon: FileText },
      { path: "/user-agreement", title: "User Agreement", description: "Community guidelines", icon: FileText },
      { path: "/privacy-policy", title: "Privacy Policy", description: "Data protection information", icon: Shield },
      { path: "/fair-play-policy", title: "Fair Play Policy", description: "Anti-cheating guidelines", icon: Shield }
    ]
  };

  return (
    <ChessAppLayout>
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <header className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">ChessHatch Sitemap</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Navigate all features of our chess learning platform. Find tutorials, puzzles, 
            community forums, and tools to improve your chess game.
          </p>
        </header>

        <div className="grid gap-8 md:grid-cols-2">
          {Object.entries(siteStructure).map(([category, pages]) => (
            <Card key={category} className="h-fit">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">{category}</CardTitle>
                <CardDescription>
                  {category === "Learning & Playing" && "Master chess through interactive learning"}
                  {category === "Community & Competition" && "Connect and compete with chess players"}
                  {category === "Account & Profile" && "Manage your chess account and settings"}
                  {category === "About & Policies" && "Platform information and guidelines"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <nav className="space-y-4">
                  {pages.map(({ path, title, description, icon: Icon }) => (
                    <Link 
                      key={path}
                      to={path}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-accent transition-colors group"
                    >
                      <Icon className="w-5 h-5 text-primary mt-0.5 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{description}</p>
                      </div>
                    </Link>
                  ))}
                </nav>
              </CardContent>
            </Card>
          ))}
        </div>

        <section className="mt-12 text-center">
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-2xl font-bold text-foreground mb-4">Need Help Finding Something?</h2>
              <p className="text-muted-foreground mb-4">
                Can't find what you're looking for? Visit our community forums or contact support.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <Link 
                  to="/forums" 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Community Forums
                </Link>
                <Link 
                  to="/about" 
                  className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-accent transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  About ChessHatch
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </ChessAppLayout>
  );
}